import { useMutation } from '@tanstack/react-query';
import { Endpoint } from '@/constants';
import { postData } from '@/utils/postData';
import {
  NotificationMessage,
  NotificationsQueue,
} from '@/components/Notifications';
import { useCheckout } from '@/contexts/checkoutContext';
import type { GooglePayOrder } from '@/types/googlePay';

interface GooglePayOrderResponse {
  isSuccess: boolean;
  data?: {
    redirectUrl?: string;
    errorMessage?: string | null;
    threeDSecureAuthenticationRequired?: boolean;
    threeDSecureRedirectUrl?: string;
  };
  messages?: string[];
  salesOrderError?: {
    message?: string;
    redirectUrl?: string;
  };
  orderNumber?: string;
}

const createGooglePayOrder = (order: GooglePayOrder): Promise<GooglePayOrderResponse> =>
  postData(Endpoint.CreateGooglePayOrder)(order as unknown as Record<string, unknown>);

interface UseGooglePayOptions {
  onThreeDSecureRequired?: (url: string) => void;
  isBuyNow?: boolean; // Add flag to indicate Buy Now mode
}

export const useGooglePay = (options?: UseGooglePayOptions) => {
  const { onThreeDSecureRequired, isBuyNow = false } = options || {};
  
  // Conditionally access checkout context - only for regular checkout, not Buy Now
  const checkoutContext = !isBuyNow ? useCheckout() : null;
  const refreshCheckoutData = checkoutContext?.refreshCheckoutData;
  
  const { isPending, mutate } = useMutation<
    GooglePayOrderResponse,
    Error,
    GooglePayOrder
  >({
    mutationFn: createGooglePayOrder,
    onError: error => {
      console.error(`Failed to submit Google Pay payment: ${error.message}`);
      NotificationsQueue.add(
        {
          notification: (
            <NotificationMessage $isError={true}>
              Payment failed. Please try again or use a different payment method.
            </NotificationMessage>
          ),
        },
        { timeout: 5000 },
      );
    },
    onSuccess: async response => {
      console.log('Google Pay: Order response received', response);
      
      // Handle sales order error
      if (response.salesOrderError) {
        const errorMessage = response.salesOrderError.message || 'Payment processing failed. Please try again.';
        NotificationsQueue.add(
          {
            notification: (
              <NotificationMessage $isError={true}>
                {errorMessage}
              </NotificationMessage>
            ),
          },
          { timeout: 5000 },
        );
        
        // If there's a redirect URL in the error, redirect after delay
        if (response.salesOrderError.redirectUrl) {
          setTimeout(() => {
            window.location.href = response.salesOrderError?.redirectUrl ?? '/checkout';
          }, 3000);
        }
        return;
      }
      
      // Handle 3D Secure
      const threeDSRequired = response.data?.threeDSecureAuthenticationRequired;
      const threeDSUrl = response.data?.threeDSecureRedirectUrl;

      if (threeDSRequired && threeDSUrl) {
        console.log('Google Pay: 3D Secure authentication required');
        if (onThreeDSecureRequired) {
          onThreeDSecureRequired(threeDSUrl);
        } else {
          // Fallback to redirect if no handler provided
          window.location.href = threeDSUrl;
        }
        return;
      }
      
      // Handle successful order with nested data structure
      if (response.isSuccess && response.data?.redirectUrl) {
        console.log('Google Pay: Order created successfully, redirecting to:', response.data.redirectUrl);
        
        // Refresh checkout data to clear the cart before redirecting (only if not Buy Now)
        if (!isBuyNow && refreshCheckoutData) {
          try {
            await refreshCheckoutData();
          } catch (error) {
            console.error('Google Pay: Failed to refresh checkout data after order', error);
          }
        }
        
        // Redirect to confirmation page
        window.location.href = response.data.redirectUrl;
        return;
      }
      
      // Handle successful order with order number (legacy response format)
      if (response.orderNumber) {
        // Build confirmation URL
        const confirmationUrl = `/checkout/order-confirmation?confirmationId=${response.orderNumber}`;
        
        // Refresh checkout data to clear the cart before redirecting (only if not Buy Now)
        if (!isBuyNow && refreshCheckoutData) {
          try {
            await refreshCheckoutData();
          } catch (error) {
            console.error('Google Pay: Failed to refresh checkout data after order', error);
          }
        }
        
        window.location.href = confirmationUrl;
        return;
      }
      
      // Handle redirect URL at root level (legacy response format)
      if (response.data?.redirectUrl) {
        // Refresh checkout data to clear the cart before redirecting (only if not Buy Now)
        if (!isBuyNow && refreshCheckoutData) {
          try {
            await refreshCheckoutData();
          } catch (error) {
            console.error('Google Pay: Failed to refresh checkout data after order', error);
          }
        }
        
        window.location.href = response.data?.redirectUrl ?? '/checkout';
        return;
      }
      
      // Handle error message in data
      if (response.data?.errorMessage) {
        NotificationsQueue.add(
          {
            notification: (
              <NotificationMessage $isError={true}>
                {response.data.errorMessage}
              </NotificationMessage>
            ),
          },
          { timeout: 5000 },
        );
        return;
      }
      
      // Handle generic failure
      if (!response.isSuccess && response.messages && response.messages.length > 0) {
        NotificationsQueue.add(
          {
            notification: (
              <NotificationMessage $isError={true}>
                {response.messages[0]}
              </NotificationMessage>
            ),
          },
          { timeout: 5000 },
        );
        return;
      }
      
      // Unexpected response
      console.error('Google Pay: Unexpected response format', response);
      NotificationsQueue.add(
        {
          notification: (
            <NotificationMessage $isError={true}>
              An unexpected error occurred. Please try again.
            </NotificationMessage>
          ),
        },
        { timeout: 5000 },
      );
    },
  });

  return { isPending, createGooglePayOrder: mutate };
};